

/**
* General helper functions
*/
var helpers = {
  /**
  * Generates a random string for when you need a unique ID in the DOM.
  * Based on: https://gist.github.com/gordonbrander/2230317
  *
  * @param prepend {string} - string to be prepended to the new id.
  * @returns {string} random string
  */
  generateID(prepend) {
    return `${prepend || ''}_${Math.random().toString(36).substr(2, 9)}`
  },

  /**
  * Returns the width of the current viewport.
  * Based on https://stackoverflow.com/a/28241682
  * @returns {number}
  */
  getScreenWidth() {
    return window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth
  },

  /**
  * Increases/decreases the element's property value by the given value amount
  * over the time amount. If a callback is present then it is executed at the
  * end of the animation.
  *
  * @NOTE: This was originally written to be used with the HorizontalScroller
  * component to modify the `scrollLeft` value. There is no guarentee that this
  * will work with any other property, so be sure to test it and modify as needed.
  *
  * @param element {dom element} element to be animated
  * @param property {string} property to change
  * @param value {number} value to change the property.
  * @param time {number} time in ms for animation
  * @param callback {function} function to execute at the end of the animation.
  */
  animate(element, property, value, time, callback) {
    const fps = 30


    const timeout = time / fps


    const amount = value / fps


    let amountLeft = Math.abs(value)

    animateFrame()

    /**
    * Recursive function to perform a portion of the property change.
    */
    function animateFrame() {
      if (amountLeft <= 0) {
        // Run the callback if one is given.
        if (callback instanceof Function) {
          callback()
        }

        return
      }
      element[property] += amount
      amountLeft -= Math.abs(amount)
      setTimeout(() => {
        animateFrame()
      }, timeout)
    }
  },

  /**
  * Returns a function, that, as long as it continues to be invoked, will not
  * be triggered. The function will be called after it stops being called for
  * N milliseconds. If `immediate` is passed, trigger the function on the
  * leading edge, instead of the trailing.
  *
  * https://davidwalsh.name/javascript-debounce-function
  * Taken from underscore.js
  * @param func {function} function to be triggered
  * @param wait {number} milliseconds to wait
  * @param immediate {bool} trigger on leading or trailing edge.
  * @returns {function}
  */
  debounce(func, wait, immediate) {
    let timeout

    return function () {
      const context = this


      const args = arguments


      const later = function () {
        timeout = null
        if (!immediate) func.apply(context, args)
      }


      const callNow = immediate && !timeout

      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
      if (callNow) func.apply(context, args)
    }
  },

  /**
  * Returns a comma separated string of the given list of producers.
  *
  * @param producers {object[]} array of objects with a `name` attribute.
  * @returns {string}
  */
  formatProducers(producers) {
    if (!producers) {
      return ''
    }

    return producers
      .map(producer => producer.name.trim())
      .join(', ')
  },

  /**
  * Takes product json data and returns it in a more useful format.
  */
  formatProductJSON(product) {
    let priceHigh = -1; let
      priceLow = Infinity

    // Format JSON to be more useful in product card components.
    product.prices = []
    product.onSale = false

    if (!product.variants && product.variants_including_master) {
      product.variants = product.variants_including_master
      delete product.variants_including_master
    }

    product.variants = product.variants
      // Only use salable variants.
      .filter(variant => variant.salable)
      // - Find the requested variant and set it as the "main" variant, or
      //   use the first variant as the "main".
      // - Find the price range for the given product.
      .map((variant, idx) => {
        const price = parseFloat(variant.price)
        const salePrices = variant.sale_prices || []
        let activeSales

        activeSales = salePrices.filter((sale_price) => {
          const startDate = Date.parse(sale_price.start_at)
          const endDate = Date.parse(sale_price.end_at) || null
          const active = startDate <= Date.now() && (endDate === null || endDate >= Date.now())

          return sale_price.enabled && active
        })

        if (price !== NaN) {
          priceHigh = priceHigh < 0 ? price : Math.max(priceHigh, price)
          priceLow = Math.min(priceLow, price)
        }

        if (idx === 0 || (product.sku && product.sku === variant.sku)) {
          product.variant = variant
        }

        if (variant['on_sale?'] || activeSales.length) {
          product.onSale = true
        }

        return variant
      })

    // Add the price range to the product.
    if (priceLow !== Infinity) {
      product.prices.push(priceLow)
    }

    if (priceHigh > -1 && priceLow !== priceHigh) {
      product.prices.push(priceHigh)
    }

    // Set the callout label to display "sale" if this product is on sale and
    // a callout label hasn't already been set.
    if (!product.calloutLabel && !product.customCallout && product.onSale) {
      product.calloutLabel = 'sale'
    }

    return product
  },

  /**
  * Wrapper for formatting multiple products with the formatProductJSON function.
  */
  formatProducts(products) {
    return products.map(product => helpers.formatProductJSON(product))
  },

  /**
  * Takes a hex color and converts it to an rgba string.
  *
  * @see https://gist.github.com/polotek/1584364
  * @param hex {string} Hex color.
  * @param opacity {number} 0-1 float.
  * @returns {string|null} null if `hex` param is invalid.
  */
  hexToRGBA(hex, opacity) {
    hex = (`${hex}`).trim()

    let r; let g; let b


    const match = hex.match(/^#?(([0-9a-zA-Z]{3}){1,3})$/)

    if (!match) {
      return null
    }

    hex = match[1]

    // check if 6 letters are provided
    if (hex.length === 6) {
      r = parseInt(hex.substring(0, 2), 16)
      g = parseInt(hex.substring(2, 4), 16)
      b = parseInt(hex.substring(4, 6), 16)
    } else if (hex.length === 3) {
      r = parseInt(hex.substring(0, 1) + hex.substring(0, 1), 16)
      g = parseInt(hex.substring(1, 2) + hex.substring(1, 2), 16)
      b = parseInt(hex.substring(2, 3) + hex.substring(2, 3), 16)
    }

    return `rgb${opacity ? 'a' : ''}(${r},${g},${b}${opacity ? `,${opacity}` : ''})`
  },

  /**
  * Recursively removes empty fields in an object.
  */
  clean(obj) {
    Object.keys(obj)
      .forEach((key) => {
        if (obj[key] && typeof obj[key] === 'object') {
          helpers.clean(obj[key])
        } else if (obj[key] === null || obj[key] === undefined) {
          delete obj[key]
        }
      })

    return obj
  },

  /**
  * Standard number formatter.
  * Adds comma separators to the nubmer passed it.
  *
  * @param num {number}
  * @param decimals {number} number of decimal places to display.
  * @returns {string|undefined}
  */
  formatNumber(num, decimals) {
    const options = {}
    const number = parseFloat(num)

    if (typeof number !== 'number') {
      return
    }

    if (typeof decimals === 'number') {
      options.minimumFractionDigits = decimals
      options.maximumFractionDigits = decimals
    }

    return number.toLocaleString(undefined, options)
  },
}

export default helpers
